import React, { JSX } from 'react';
import {
  Checkbox,
  Input,
  InputChangeEvent,
  RadioButton
} from '@progress/kendo-react-inputs';
import { Label } from '@progress/kendo-react-labels';
import { Button } from '@progress/kendo-react-buttons';
import { useSessionStorage } from 'usehooks-ts';

import {
  GetResourceByTypes,
  isSystemDefaultResource,
  sortResourcesWithSystemLast
} from '../../utils/game-document';
import { GameDocumentContext } from '../../contexts/game-document';
import { ResourceEntity } from '../../types/game-document/entities';

export const ResourceTab: React.FC<{
  fields: JSX.Element;
  mediaType: string;
  onChange: (value: string) => void;
}> = ({ mediaType, fields, onChange }) => {
  const [state] = React.useContext(GameDocumentContext);

  const [resources, setResources] = React.useState<ResourceEntity[]>([]);
  const [selectedId, setSelectedId] = React.useState<string>();
  const [searchText, setSearchText] = React.useState<string>('');
  const [showSystemDefaults, setShowSystemDefaults] = useSessionStorage<boolean>(
    'show-system-default-resources',
    false
  );

  const selectedResource = React.useMemo(() => {
    if (!selectedId) return;
    return resources.find((r) => r.id === selectedId);
  }, [selectedId, resources]);

  const onChangeSearchResource = (e: InputChangeEvent) => {
    setSearchText((e.target.value as string) ?? '');
  };

  React.useEffect(() => {
    if (state.gameDocument) {
      let resourcesData =
        GetResourceByTypes(state.gameDocument!, [mediaType]).filter(
          (item) => item.name
        ) ?? [];

      const query = searchText.trim().toLowerCase();
      if (query.length > 0) {
        resourcesData = resourcesData.filter((item) =>
          item.name.toLowerCase().includes(query)
        );
      }

      if (!showSystemDefaults) {
        resourcesData = resourcesData.filter(
          (r) => !isSystemDefaultResource(r, state.gameDocument)
        );
      } else {
        resourcesData = sortResourcesWithSystemLast(
          resourcesData,
          state.gameDocument
        );
      }

      setResources(resourcesData);
    }
  }, [mediaType, state.gameDocument, searchText, showSystemDefaults]);

  return (
    <>
      {!selectedId && (
        <>
          <div className={'d-flex align-items-center gap-2 mb-2'}>
            <Input
              type="text"
              placeholder="search"
              value={searchText}
              onChange={onChangeSearchResource}
            />
            <Checkbox
              label={'Show system defaults'}
              checked={showSystemDefaults}
              onChange={(e) => setShowSystemDefaults(Boolean(e.value))}
            />
          </div>
          <div className={'d-flex flex-column'}>
            {resources.map((item) => (
              <div className={'d-flex flex-column gap-1 p-2 border-bottom'}>
                <div key={item.id} className={'d-flex align-items-center'}>
                  <div className={'d-flex h-100'}>
                    <RadioButton
                      name={'resource-list'}
                      className={'d-flex align-items-center'}
                      id={item.id}
                      value={item.id}
                      onChange={(e) => {
                        if (item.value) {
                          setSelectedId(e.value);
                          onChange(item.value);
                        }
                      }}
                      checked={item.id === selectedId}
                    />
                  </div>
                  <Label
                    editorId={item.id}
                    className="d-flex align-items-center cursor-pointer text-decoration-none fw-normal">
                    <div className={'cell-thumbnail-container mx-2'}>
                      {item.type === 'image' ? (
                        <img
                          className={'w-100 h-100'}
                          src={item.value}
                          alt=""
                        />
                      ) : (
                        <video className={'rounded w-100 h-100'}>
                          <source src={item.value} type={'video/mp4'} />
                        </video>
                      )}
                    </div>
                    <span>{item.name}</span>
                  </Label>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {selectedId && (
        <div className="k-edit-form-container pt-3 pb-3">
          <div className="k-edit-label">
            <label htmlFor="k-editor-image-url">Image</label>
          </div>
          <div className="k-edit-field d-flex flex-column gap-2">
            <div className={'cell-thumbnail-container'}>
              {selectedResource?.type === 'image' ? (
                <img
                  className={'w-100 h-100'}
                  src={selectedResource.value}
                  alt=""
                />
              ) : (
                <video className={'rounded w-100 h-100'}>
                  <source src={selectedResource?.value} type={'video/mp4'} />
                </video>
              )}
            </div>
            <Button onClick={() => setSelectedId(undefined)}>Change</Button>
          </div>
          {fields}
        </div>
      )}
    </>
  );
};
